import './code-highlight-block.css';

import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { mergeClasses, isExternalLink } from 'minimal-shared/utils';

import Link from '@mui/material/Link';

import { RouterLink } from 'src/routes/components';

import { RADIUS } from 'src/theme/ui-tokens';

import { Image } from '../image';
import { MarkdownRoot } from './styles';
import { markdownClasses } from './classes';
import { htmlToMarkdown, isMarkdownContent } from './html-to-markdown';
import { MARKDOWN_REMARK_PLUGINS, MARKDOWN_REHYPE_PLUGINS } from './markdown-plugins';


// ----------------------------------------------------------------------

/**
 * 마크다운·HTML 본문 렌더러.
 *
 * ⚠️ **`sx`·`className` 은 선택이다** — 좁히면 `<Markdown children={content} />` 가
 * TS2739 로 막힌다. `...other` 가 그대로 넘어가므로 색인 서명도 연다.
 */
export type MarkdownProps = {
  children?: React.ReactNode;
  sx?: any;
  className?: string;
  /**
   * 기본 그림표에 **얹는** 태그들 — 같은 이름이면 이쪽이 이긴다.
   * 🔑 `components` 를 통째로 넘기면 기본 그림표(링크·이미지·표)가 통째로 사라진다.
   *   얹는 자리를 따로 둔 까닭이다(근거 쪽지 같은 제 태그가 이 길로 붙는다).
   */
  extraComponents?: Record<string, any>;
} & { [key: string]: any };

export function Markdown({ children, sx, className, extraComponents, ...other }: MarkdownProps) {
  const content = useMemo(() => {
    if (isMarkdownContent(`${children}`)) {
      return children;
    }
    return htmlToMarkdown(`${children}`.trim());
  }, [children]);

  return (
    <MarkdownRoot className={mergeClasses([markdownClasses.root, className])} sx={sx}>
      <ReactMarkdown
        children={content as string}
        components={extraComponents ? { ...components, ...extraComponents } : components}
        remarkPlugins={MARKDOWN_REMARK_PLUGINS}
        rehypePlugins={MARKDOWN_REHYPE_PLUGINS}
        /* base64-encoded images
         * https://github.com/remarkjs/react-markdown/issues/774
         * urlTransform={(value: string) => value}
         */
        {...other}
      />
    </MarkdownRoot>
  );
}

const components = {
  img: ({ ...other }: any) => (
    <Image
      // ratio="16/9"
      ratio="4/5"
      className={markdownClasses.content.image}
      sx={{ borderRadius: RADIUS.lg, aspectRatio: '4/5' }}
      {...other}
    />
  ),
  a: ({ href, children, node, ...other }: any) => {
    const linkProps = isExternalLink(href)
      ? { target: '_blank', rel: 'noopener' }
      : { component: RouterLink };

    return (
      <Link {...linkProps} href={href} className={markdownClasses.content.link} {...other}>
        {children}
      </Link>
    );
  },
  pre: ({ children }: any) => (
    <div className={markdownClasses.content.codeBlock}>
      <pre>{children}</pre>
    </div>
  ),
  code({ className, children, node, ...other }: any) {
    const language = /language-(\w+)/.exec(className || '');

    return language ? (
      <code {...other} className={className}>
        {children}
      </code>
    ) : (
      <code {...other} className={markdownClasses.content.codeInline}>
        {children}
      </code>
    );
  },
};
